import { Grid } from "@mui/material";
import { usePostHog } from "posthog-js/react";
import { useState } from "react";
import { isValidPhoneNumber } from "react-phone-number-input";
import { useDispatch } from "react-redux";
import * as yup from "yup";
import useTriggerSnackbar from "../../../hooks/useTriggerSnackbar";
import { registerHiredlyX } from "../../../redux/actions/user_action";
import SharedSelectField from "./SharedSelectField";
import SharedTextField from "./SharedTextField";
import styles from "./phoneInput.module.css";
import {
  ButtonStyled,
  CardContainer,
  CardImage,
  CardListContainer,
  CardText,
  CardTextContainer,
  ErrorTextStyled,
  ExclamationCircleIconStyled,
  FieldContent,
  GridContainerStyled,
  InputTitle,
  InputWrapper,
  PhoneInputStyled,
} from "./styles";

export default function HXRegister(props) {
  const {} = props;

  const dispatch = useDispatch();
  const [submitLoading, setSubmitLoading] = useState(false);
  const [triggerSnackbarFunc] = useTriggerSnackbar();

  const dropdownCountry = "MY";
  const dropdownList = [
    { id: "up to 10 employees", title: "Up to 10 employees" },
    { id: "11 to 50 employees", title: "11 to 50 employees" },
    { id: "51 to 200 employees", title: "51 to 200 employees" },
    { id: "201 to 500 employees", title: "201 to 500 employees" },
    { id: "501 to 1000 employees", title: "501 to 1000 employees" },
    { id: "1001 to 2000 employees", title: "1001 to 2000 employees" },
    { id: "2001 to 5000 employees", title: "2001 to 5000 employees" },
    { id: "more than 5000 employees", title: "More than 5000 employees" },
  ];

  const validationSchema = yup.object().shape({
    companyName: yup.string().required("Company name is required"),
    firstName: yup.string().required("First name is required"),
    lastName: yup.string().required("Last name is required"),
    email: yup
      .string()
      .email("Invalid email format")
      .required("Email is required"),
    phoneNumber: yup
      .string()
      .required("Contact number is required")
      .test(
        "is-valid-phone-number",
        "Please enter a valid phone number",
        (value) => (value ? isValidPhoneNumber(value) : false)
      ),
    companySize: yup.string().required("This field is required"),
    message: yup.string().required("This field is required"),
  });

  const [errors, setErrors] = useState({});
  const [values, setValues] = useState({
    firstName: "",
    lastName: "",
    email: "",
    companyName: "",
    leadSource: "Hiredly X Landing page",
    segment: "hiredly-x-landing-page",
    phoneNumber: "",
    companySize: "",
    message: "",
  });

  const buttonCTA = "Submit";
  const buttonColour = "yellow";
  const posthog = usePostHog();

  // For registration logic
  const handleChange = (e) => {
    const { name, value } = e.target;
    let data = value;

    setErrors({ ...errors, [name]: "" });
    setValues({ ...values, [name]: data });
  };

  const handlePhoneNumber = (number) => {
    setValues((prev) => ({
      ...prev,
      ["phoneNumber"]: number,
    }));
    setErrors({ ...errors, ["phoneNumber"]: "" });
  };

  const handleSubmit = async () => {
    // e.preventDefault();
    try {
      await validationSchema.validate(values, { abortEarly: false });
      onSubmit(values);
    } catch (error) {
      const validationErrors = {};
      error.inner.forEach((err) => {
        validationErrors[err.path] = err.message;
      });
      setErrors(validationErrors);
    }
  };

  const onSubmit = async (values) => {
    setSubmitLoading(true);
    try {
      // Register the company
      const registerResponse = await registerCompany(values);
      setSubmitLoading(false);

      if (registerResponse?.data?.leadCreated) {
        if (
          typeof window !== "undefined" &&
          (window?.location?.hostname === "my.hiredly.com" ||
            window?.location?.hostname === "sg.hiredly.com") &&
          typeof window.gtag_report_conversion === "function"
        ) {
          const host = window.location.hostname;
          const targetUrl =
            host === "sg.hiredly.com"
              ? "https://sg.hiredly.com/hiredly-x"
              : "https://my.hiredly.com/hiredly-x";
          window.gtag_report_conversion(targetUrl);
        }

        triggerSnackbarFunc({
          snackbarMessage: "Form Submitted Successfully",
          severity: "success",
        });

        setTimeout(() => {
          // clear form values after submitted successfully
          setValues({
            firstName: "",
            lastName: "",
            email: "",
            companyName: "",
            leadSource: "Hiredly X Landing page",
            segment: "hiredly-x-landing-page",
            phoneNumber: "",
            companySize: "",
            message: "",
          });
          setErrors({});
        }, 1000);
      } else {
        triggerSnackbarFunc({
          snackbarMessage: error?.message || "Sign up error",
          severity: "error",
        });
      }
    } catch (error) {
      triggerSnackbarFunc({
        snackbarMessage: error?.response?.data || "Sign up error",
        severity: "error",
      });
    }
  };

  const registerCompany = async (values) => {
    return await dispatch(
      registerHiredlyX({
        input: {
          firstName: values?.firstName,
          lastName: values?.lastName,
          email: values?.email,
          companyName: values?.companyName,
          leadSource: "Hiredly X Landing page",
          segment: "hiredly-x-landing-page",
          phoneNumber: values?.phoneNumber,
          companySize: values?.companySize,
          message: values?.message,
        },
      })
    );
  };

  const ErrorText = ({ children }) => {
    if (!children) {
      return null;
    }

    return (
      <Grid
        sx={{
          display: "flex",
          flexDirection: "row",
          gap: "6px",
          alignItems: "center",
          paddingTop: "2px",
        }}
      >
        <ExclamationCircleIconStyled />{" "}
        <ErrorTextStyled>{children}</ErrorTextStyled>
      </Grid>
    );
  };

  return (
    <GridContainerStyled>
      <CardContainer>
        <CardListContainer>
          <CardImage
            src={`https://assets.hiredly.com/cms/discussing_data_with_coworkers_407d99b916.jpg`}
          />
          <CardTextContainer>
            <CardText type={"title"}>
              Book Your Free Consultation Today!
            </CardText>

            <Grid
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "16px",
                alignItems: "flex-start",
                width: "100%",
              }}
            >
              <Grid
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "12px",
                  alignItems: "flex-start",
                  width: "100%",
                }}
              >
                <FieldContent>
                  <InputWrapper>
                    <InputTitle>First name*</InputTitle>
                    <SharedTextField
                      name="firstName"
                      value={values.firstName}
                      onChange={handleChange}
                      error={errors.firstName}
                      variants={"shortWhite"}
                    />
                    <ErrorText>{errors.firstName}</ErrorText>
                  </InputWrapper>

                  <InputWrapper>
                    <InputTitle>Last name*</InputTitle>
                    <SharedTextField
                      name="lastName"
                      value={values.lastName}
                      onChange={handleChange}
                      error={errors.lastName}
                      variants={"shortWhite"}
                    />
                    <ErrorText>{errors.lastName}</ErrorText>
                  </InputWrapper>
                </FieldContent>

                <InputWrapper>
                  <InputTitle>Business Email Address*</InputTitle>
                  <SharedTextField
                    // type="email"
                    name="email"
                    value={values.email}
                    onChange={handleChange}
                    error={errors.email}
                    variants={"shortWhite"}
                  />
                  {errors?.email?.includes("contact") ? (
                    <ErrorText>
                      Company name or email domain already exists, please{" "}
                      <span
                        onClick={() =>
                          dispatch(
                            updateContactUsEmployer({
                              state: true,
                              origin: "head-banner",
                            })
                          )
                        }
                        style={{
                          textDecoration: "underline",
                          cursor: "pointer",
                        }}
                      >
                        contact us
                      </span>{" "}
                      for assistance{" "}
                    </ErrorText>
                  ) : (
                    <ErrorText>{errors.email}</ErrorText>
                  )}
                </InputWrapper>

                <InputWrapper>
                  <InputTitle>Company name*</InputTitle>
                  <SharedTextField
                    name="companyName"
                    value={values.companyName}
                    onChange={handleChange}
                    error={errors.companyName}
                    variants={"shortWhite"}
                  />

                  {errors?.companyName?.includes("contact") ? (
                    <ErrorText>
                      Company name or email domain already exists, please{" "}
                      <span
                        onClick={() =>
                          dispatch(
                            updateContactUsEmployer({
                              state: true,
                              origin: "head-banner",
                            })
                          )
                        }
                        style={{
                          textDecoration: "underline",
                          cursor: "pointer",
                        }}
                      >
                        contact us
                      </span>{" "}
                      for assistance{" "}
                    </ErrorText>
                  ) : (
                    <ErrorText>{errors.companyName}</ErrorText>
                  )}
                </InputWrapper>

                <InputWrapper sx={{ position: "relative" }}>
                  <InputTitle>Phone number*</InputTitle>
                  <PhoneInputStyled
                    className={
                      errors.phoneNumber
                        ? styles["custom-phone-input-new-error"]
                        : styles["custom-phone-input-new"]
                    }
                    style={{ width: "100%" }}
                    error={errors.phoneNumber}
                    placeholder="+6012 3456789"
                    name="phoneNumber"
                    value={values.phoneNumber}
                    defaultCountry={dropdownCountry ?? "MY"}
                    countryCallingCodeEditable={false}
                    international
                    onChange={handlePhoneNumber}
                  />
                  <ErrorText>{errors.phoneNumber}</ErrorText>
                </InputWrapper>

                <InputWrapper>
                  <InputTitle>Company Headcount Size*</InputTitle>
                  <SharedSelectField
                    options={dropdownList}
                    name="companySize"
                    value={values.companySize}
                    onChange={handleChange}
                    error={errors.companySize}
                    variants={"shortWhite"}
                  />
                  <ErrorText>{errors.companySize}</ErrorText>
                </InputWrapper>

                <InputWrapper>
                  <InputTitle>What roles are you hiring for?*</InputTitle>
                  <SharedTextField
                    name="message"
                    placeholder= "Please mention your industry (Commercial, Tech, or Engineering)."
                    value={values.message}
                    onChange={handleChange}
                    error={errors.message}
                    variants={"shortWhite"}
                  />
                  <ErrorText>{errors.message}</ErrorText>
                </InputWrapper>
              </Grid>
            </Grid>

            <ButtonStyled
              disabled={submitLoading}
              variant="contained"
              onClick={() => handleSubmit()}
            >
              {buttonCTA}
            </ButtonStyled>
          </CardTextContainer>
        </CardListContainer>
      </CardContainer>
    </GridContainerStyled>
  );
}
